import { isStageAvailable, type FeatureStage, type IntegrationCredential } from '@protoss/shared';
import {
  Button,
  CheckCircledIcon,
  GearIcon,
  IconSize,
  PersonIcon,
  ReloadIcon,
  Share1Icon,
  SkeletonBlock,
  SkeletonProvider,
  SkeletonText,
  TokensIcon,
  ZoomIcon,
} from '@protoss/ui';
import { Avatar } from '@protoss/ui/components';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { AppShell, MainContentShell } from '../components/shell';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { AiCreditsReport } from '../features/ai-credits/AiCreditsReport';
import { ConnectedAppsSection } from '../features/connected-apps/ConnectedAppsSection';
import { useLiveMeetingSessionContext } from '../features/live-meeting/contexts/LiveMeetingSessionContext';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { apiFetch } from '../lib/apiClient';
import { StageBadge } from '../lib/featureStage';
import { friendlyErrorMessage } from '../lib/error';
import { NotFoundPage } from './NotFoundPage';
import './settings-page.css';

// ─── Settings menu icon map ────────────────────────────────────────────────

const SETTINGS_ICON_MAP: Record<string, React.ReactNode> = {
  person: <PersonIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  'share-1': <Share1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  tokens: <TokensIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
  gear: <GearIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />,
};

function resolveSettingsMenuIcon(iconKey: string): React.ReactNode {
  return (
    SETTINGS_ICON_MAP[iconKey] ?? (
      <GearIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
    )
  );
}

// ─── Settings menu items ───────────────────────────────────────────────────

const SETTINGS_MENU_ITEMS: {
  key: string;
  label: string;
  routeSegment: string;
  iconKey: string;
  // Feature maturity stage. Absent = ga (always shown). 'alpha' shows only in
  // local/dev (workspace featureMinStage alpha/unset) with an alpha badge.
  stage?: FeatureStage;
}[] = [
  { key: 'me', label: 'Profile', routeSegment: 'me', iconKey: 'person' },
  { key: 'integrations', label: 'Integrations', routeSegment: 'integrations', iconKey: 'share-1' },
  { key: 'connected-apps', label: 'Connected apps', routeSegment: 'connected-apps', iconKey: 'share-1' },
  { key: 'ai-credits', label: 'AI Credits', routeSegment: 'ai-credits', iconKey: 'tokens', stage: 'alpha' },
];

// ─── Profile section ───────────────────────────────────────────────────────

/**
 * Profile - shows the current user's identity, workspace role, modules, and security options.
 * Password reset is hidden for SSO users (authProvider !== 'password').
 */
function ProfileSection() {
  const { user, appUser, sendPasswordReset } = useAuth();
  const { workspace, modules } = useWorkspace();
  const isAdmin = useWorkspaceAdmin(workspace);

  const [resetLoading, setResetLoading] = React.useState(false);
  const [resetSent, setResetSent] = React.useState(false);
  const [resetError, setResetError] = React.useState<string | null>(null);

  const enabledModules = modules.filter((m) => m.isEnabled);
  const isPasswordAuth = appUser?.authProvider === 'password';

  const avatarSrc = appUser?.avatarUrl ?? user?.photoURL ?? null;
  const displayName = appUser?.displayName ?? user?.displayName ?? 'User';
  const email = appUser?.email ?? user?.email ?? '';

  async function handlePasswordReset() {
    if (!email) return;
    setResetLoading(true);
    setResetError(null);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      setResetError(friendlyErrorMessage(err, 'Failed to send the reset email. Please try again.'));
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <SkeletonProvider loading={appUser === null}>
      <div className="settings-profile">
        {/* Hero */}
        <div className="settings-profile-hero">
          <SkeletonBlock className="settings-profile-avatar-skeleton">
            <Avatar src={avatarSrc} name={displayName} size="lg" />
          </SkeletonBlock>
          <div className="settings-profile-hero-info">
            <SkeletonBlock className="settings-profile-name-skeleton">
              <p className="settings-profile-name">{displayName}</p>
            </SkeletonBlock>
            <SkeletonBlock className="settings-profile-email-skeleton">
              <p className="settings-profile-email">{email}</p>
            </SkeletonBlock>
          </div>
        </div>

        {/* Workspace */}
        <div className="settings-profile-group">
          <h2 className="settings-profile-group-title">Workspace</h2>

          <div className="settings-profile-row">
            <span className="settings-profile-row-label">Status</span>
            <span className="settings-profile-row-value">
              <SkeletonBlock className="settings-profile-value-skeleton">
                {isAdmin ? (
                  <span className="settings-profile-badge settings-profile-badge--admin">
                    Admin
                  </span>
                ) : (
                  <span className="settings-profile-badge">Member</span>
                )}
              </SkeletonBlock>
            </span>
          </div>

          {enabledModules.length > 1 && (
            <div className="settings-profile-row">
              <span className="settings-profile-row-label">Modules</span>
              <span className="settings-profile-row-value">
                <SkeletonBlock className="settings-profile-value-skeleton">
                  <ul className="settings-module-list">
                    {enabledModules.map((m) => {
                      const roleDisplay = m.userRole || (isAdmin ? 'Admin' : null);
                      return (
                        <li key={m.key} className="settings-profile-badge">
                          {m.label}
                          {roleDisplay && (
                            <span className="settings-module-role">
                              {roleDisplay === 'admin' ? ' Admin' : ` (${roleDisplay})`}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </SkeletonBlock>
              </span>
            </div>
          )}

          {enabledModules.length === 1 && enabledModules[0] && (
            <div className="settings-profile-row">
              <span className="settings-profile-row-label">Role</span>
              <span className="settings-profile-row-value">
                <SkeletonBlock className="settings-profile-value-skeleton">
                  {enabledModules[0].userRole || isAdmin ? (
                    <span className="settings-profile-badge">
                      {enabledModules[0].userRole || (isAdmin ? 'Admin' : 'Not assigned')}
                    </span>
                  ) : (
                    <span className="settings-profile-badge">Not assigned</span>
                  )}
                </SkeletonBlock>
              </span>
            </div>
          )}
        </div>

        {/* Security - only for password auth users */}
        {isPasswordAuth && (
          <div className="settings-profile-group">
            <h2 className="settings-profile-group-title">Security</h2>

            <div className="settings-security-row">
              <div className="settings-security-info">
                <p className="settings-security-label">Password</p>
                {resetSent ? (
                  <p className="settings-security-hint settings-security-hint--success">
                    Reset email sent - check your inbox.
                  </p>
                ) : (
                  <p className="settings-security-hint">We'll send a reset link to {email}.</p>
                )}
                {resetError && (
                  <p className="settings-security-hint settings-security-hint--error">
                    {resetError}
                  </p>
                )}
              </div>
              {!resetSent ? (
                <Button
                  variant="secondary"
                  onClick={() => void handlePasswordReset()}
                  disabled={resetLoading}
                >
                  {resetLoading ? (
                    <>
                      <ReloadIcon
                        width={IconSize.sm}
                        height={IconSize.sm}
                        aria-hidden="true"
                        className="settings-btn-icon"
                      />
                      Sending…
                    </>
                  ) : (
                    'Send reset email'
                  )}
                </Button>
              ) : (
                <Button variant="ghost" disabled aria-disabled="true">
                  <CheckCircledIcon
                    width={IconSize.sm}
                    height={IconSize.sm}
                    aria-hidden="true"
                    className="settings-btn-icon"
                  />
                  Sent
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </SkeletonProvider>
  );
}

// ─── Connections section ───────────────────────────────────────────────────

/**
 * Connections - lets users manage personal OAuth connections (e.g. Zoom).
 */
type ZoomStatus = { connected: boolean; connectedAt: string | null };

// Single reducer owns both phase AND status - updates are always atomic.
// No separate useState calls means no "wrong combination" intermediate render.
interface ZoomState {
  phase:
    | 'init' // page loading, status unknown
    | 'idle' // ready
    | 'connect-fetching' // fetching OAuth URL
    | 'connect-awaiting' // popup open, waiting for user
    | 'connect-reloading' // popup done, reloading status
    | 'disconnecting'; // DELETE in progress
  status: ZoomStatus | null;
  error: string | null;
}
type ZoomAction =
  | { type: 'LOADED'; status: ZoomStatus }
  | { type: 'LOAD_FAILED'; error: string }
  | { type: 'CONNECT_FETCH' }
  | { type: 'POPUP_OPENED' }
  | { type: 'POPUP_DISMISSED' }
  | { type: 'RELOADING' }
  | { type: 'OAUTH_DONE'; status: ZoomStatus } // atomic: phase+status in one dispatch
  | { type: 'OAUTH_FAILED'; error: string }
  | { type: 'DISCONNECTING' }
  | { type: 'DISCONNECTED'; status: ZoomStatus } // atomic: phase+status in one dispatch
  | { type: 'ACTION_FAILED'; error: string };

function zoomReducer(state: ZoomState, action: ZoomAction): ZoomState {
  switch (action.type) {
    case 'LOADED':
      return { phase: 'idle', status: action.status, error: null };
    case 'LOAD_FAILED':
      return { phase: 'idle', status: null, error: action.error };
    case 'CONNECT_FETCH':
      return { ...state, phase: 'connect-fetching', error: null };
    case 'POPUP_OPENED':
      return { ...state, phase: 'connect-awaiting' };
    case 'POPUP_DISMISSED':
      return state.phase === 'connect-awaiting' ? { ...state, phase: 'idle' } : state;
    case 'RELOADING':
      return { ...state, phase: 'connect-reloading' };
    case 'OAUTH_DONE':
      return { phase: 'idle', status: action.status, error: null };
    case 'OAUTH_FAILED':
      return { ...state, phase: 'idle', error: action.error };
    case 'DISCONNECTING':
      return { ...state, phase: 'disconnecting', error: null };
    case 'DISCONNECTED':
      return { phase: 'idle', status: action.status, error: null };
    case 'ACTION_FAILED':
      return { ...state, phase: 'idle', error: action.error };
  }
}

function ConnectionsSection() {
  const [{ phase, status, error }, dispatch] = React.useReducer(zoomReducer, {
    phase: 'init',
    status: null,
    error: null,
  });
  const [disconnectOpen, setDisconnectOpen] = React.useState(false);
  const popupRef = React.useRef<Window | null>(null);
  // null = loading, true = workspace has active Zoom integration, false = not enabled
  const [workspaceZoomEnabled, setWorkspaceZoomEnabled] = React.useState<boolean | null>(null);
  const liveMeetingSession = useLiveMeetingSessionContext();
  const refreshZoomStatus = liveMeetingSession?.refreshZoomStatus;

  // Initial status load
  React.useEffect(() => {
    void (async () => {
      try {
        const [zoomStatus, integrationsResult] = await Promise.all([
          apiFetch<ZoomStatus>('/api/zoom/user/status'),
          apiFetch<{ integrations: IntegrationCredential[] }>('/api/integration'),
        ]);
        const hasZoom = integrationsResult.integrations.some(
          (c) => c.definition.slug === 'zoom' && c.lifecycleStatus === 'active'
        );
        setWorkspaceZoomEnabled(hasZoom);
        dispatch({ type: 'LOADED', status: zoomStatus });
      } catch (err) {
        setWorkspaceZoomEnabled(false);
        dispatch({
          type: 'LOAD_FAILED',
          error: friendlyErrorMessage(err, 'Failed to load the Zoom connection status.'),
        });
      }
    })();
  }, []);

  // Detect popup closure. Only attached during connect-awaiting phase.
  // If BroadcastChannel fires first, the BC handler transitions phase to connect-reloading
  // and this effect is torn down before the focus event fires - no double-reload.
  // If BroadcastChannel never arrives (e.g., on cosow-dev), this fallback reloads status
  // so the UI reflects the actual OAuth outcome without requiring a page refresh.
  React.useEffect(() => {
    if (phase !== 'connect-awaiting') return;
    const handleFocus = () => {
      if (popupRef.current?.closed) {
        popupRef.current = null;
        dispatch({ type: 'RELOADING' });
        void (async () => {
          try {
            const s = await apiFetch<ZoomStatus>('/api/zoom/user/status');
            dispatch({ type: 'OAUTH_DONE', status: s });
            refreshZoomStatus?.();
          } catch (err) {
            dispatch({
              type: 'ACTION_FAILED',
              error: friendlyErrorMessage(err, 'Failed to refresh the connection status.'),
            });
          }
        })();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [phase, refreshZoomStatus]);

  // BroadcastChannel + postMessage fallback listener
  React.useEffect(() => {
    function handleOAuthResult(data: { type: string; error?: string | null }) {
      if (data.type === 'ZOOM_OAUTH_SUCCESS') {
        popupRef.current = null;
        dispatch({ type: 'RELOADING' });
        void (async () => {
          try {
            const s = await apiFetch<ZoomStatus>('/api/zoom/user/status');
            dispatch({ type: 'OAUTH_DONE', status: s });
            refreshZoomStatus?.();
          } catch (err) {
            dispatch({
              type: 'OAUTH_FAILED',
              error: friendlyErrorMessage(err, 'Failed to refresh the connection status.'),
            });
          }
        })();
      } else if (data.type === 'ZOOM_OAUTH_ERROR') {
        popupRef.current = null;
        const errorCode = decodeURIComponent((data.error as string | null) ?? 'unknown');
        dispatch({
          type: 'OAUTH_FAILED',
          error: `Zoom connection failed: ${errorCode.replace(/_/g, ' ')}.`,
        });
      }
    }

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('zoom-oauth');
      bc.onmessage = (event: MessageEvent) =>
        handleOAuthResult(event.data as { type: string; error?: string | null });
    } catch {
      // BroadcastChannel not supported - rely on postMessage fallback
    }
    function handleWindowMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      handleOAuthResult(event.data as { type: string; error?: string | null });
    }
    window.addEventListener('message', handleWindowMessage);
    return () => {
      bc?.close();
      window.removeEventListener('message', handleWindowMessage);
    };
  }, []);

  async function handleConnect() {
    dispatch({ type: 'CONNECT_FETCH' });
    try {
      const data = await apiFetch<{ url: string }>('/api/zoom/user/connect');
      const popup = window.open(data.url, 'zoom-user-oauth', 'width=600,height=700,popup=yes');
      if (popup) {
        popupRef.current = popup;
        dispatch({ type: 'POPUP_OPENED' });
      } else {
        dispatch({ type: 'ACTION_FAILED', error: 'Popup was blocked by the browser.' });
      }
    } catch (err) {
      dispatch({
        type: 'ACTION_FAILED',
        error: friendlyErrorMessage(err, 'Failed to start the Zoom connection. Please try again.'),
      });
    }
  }

  async function handleDisconnect() {
    dispatch({ type: 'DISCONNECTING' });
    try {
      await apiFetch('/api/zoom/user/connection', { method: 'DELETE' });
      const data = await apiFetch<ZoomStatus>('/api/zoom/user/status');
      dispatch({ type: 'DISCONNECTED', status: data });
      refreshZoomStatus?.();
    } catch (err) {
      dispatch({
        type: 'ACTION_FAILED',
        error: friendlyErrorMessage(err, 'Failed to disconnect Zoom. Please try again.'),
      });
    }
  }

  let cardAction: React.ReactNode;
  switch (phase) {
    case 'init':
      cardAction = null;
      break;
    case 'connect-fetching':
      cardAction = (
        <Button variant="primary" size="sm" disabled>
          Connecting…
        </Button>
      );
      break;
    case 'connect-awaiting':
    case 'connect-reloading':
      cardAction = (
        <Button variant="ghost" size="sm" disabled>
          Awaiting authorization…
        </Button>
      );
      break;
    case 'disconnecting':
      cardAction = (
        <Button variant="secondary" size="sm" disabled>
          Disconnecting…
        </Button>
      );
      break;
    default: // 'idle'
      cardAction = status?.connected ? (
        <Button variant="secondary" size="sm" onClick={() => setDisconnectOpen(true)}>
          Disconnect
        </Button>
      ) : (
        <Button variant="primary" size="sm" onClick={() => void handleConnect()}>
          Connect
        </Button>
      );
  }

  // Still loading - show skeleton card
  if (workspaceZoomEnabled === null) {
    return (
      <div className="settings-connections">
        <div className="settings-connections-card">
          <div className="settings-connections-card-header">
            <SkeletonBlock
              width="32px"
              height="32px"
              radius="var(--radius-sm)"
              className="settings-connections-skeleton-icon"
            />
            <div className="settings-connections-card-info">
              <SkeletonBlock width="40px" height="0.875em" />
              <SkeletonBlock width="120px" height="0.75em" />
            </div>
          </div>
          <SkeletonText lines={3} lastLineWidth="55%" />
          <div className="settings-connections-card-actions">
            <SkeletonBlock width="80px" height="28px" />
          </div>
        </div>
      </div>
    );
  }

  // Zoom not enabled for this workspace - no connections to manage
  if (!workspaceZoomEnabled) {
    return (
      <p className="settings-connections-empty">
        No personal connections are available for this workspace.
      </p>
    );
  }

  return (
    <>
      <div className="settings-connections">
        <div className="settings-connections-card">
          <div className="settings-connections-card-header">
            <span className="settings-connections-card-icon">
              <ZoomIcon size={32} />
            </span>
            <div className="settings-connections-card-info">
              <span className="settings-connections-card-name">Zoom</span>
              {status?.connected && status.connectedAt && (
                <span className="settings-connections-card-status">
                  Connected · {new Date(status.connectedAt).toLocaleDateString()}
                </span>
              )}
              {status?.connected === false && (
                <span className="settings-connections-card-status">Not connected</span>
              )}
            </div>
          </div>
          <p className="settings-connections-card-desc">
            Connect your personal Zoom account to enable live meeting intelligence.
          </p>
          {error && <p className="settings-connections-card-error">{error}</p>}
          {cardAction && <div className="settings-connections-card-actions">{cardAction}</div>}
        </div>
      </div>

      <ConfirmDeleteDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect Zoom?"
        confirmKeyword="Zoom"
        description="Your Zoom account will be unlinked and live meeting intelligence will stop working."
        confirmLabel="Disconnect"
        pendingLabel="Disconnecting…"
        onConfirm={() => {
          setDisconnectOpen(false);
          void handleDisconnect();
        }}
      />
    </>
  );
}

// ─── Page content ──────────────────────────────────────────────────────────

/**
 * SettingsPageContent - user settings interface with AppShell.
 *
 * Module-agnostic - lives at /settings, accessible to any authenticated user.
 *
 * Layout:
 *   - LeftNavPane: shared AppNav (no primary item selected)
 *   - ContextPane: Settings sub-section menu
 *   - MainPane: active section content
 */
function SettingsPageContent() {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const { workspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);

  // Auto-show context pane; reset on unmount (handled by the hook).
  useLayoutConfig({ showContextPane: true });

  // Default section redirect
  React.useEffect(() => {
    if (!section) {
      navigate('/settings/me', { replace: true });
    }
  }, [section, navigate]);

  const selectedSection = section ?? 'me';
  // Stage-gate menu items (e.g. AI Credits is alpha-only): show in local/dev, hide in uat/prod.
  const visibleMenuItems = SETTINGS_MENU_ITEMS.filter((item) =>
    isStageAvailable(item.stage, workspace?.featureMinStage)
  );
  const isKnownSection =
    !section || visibleMenuItems.some((item) => item.routeSegment === section);

  const navigateToSection = (routeSegment: string) => {
    navigate(`/settings/${routeSegment}`);
  };

  // Left nav
  const leftNav = (
    <AppNav
      selected={null}
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => void navigate('/administration')}
    />
  );

  // Context pane - settings menu
  const contextPane = (
    <div className="settings-menu">
      <div className="settings-menu-header">
        <h3 className="settings-menu-title">Settings</h3>
      </div>
      <nav className="settings-menu-nav" aria-label="Settings menu">
        {visibleMenuItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className="settings-menu-item"
            data-selected={selectedSection === item.routeSegment || undefined}
            onClick={() => navigateToSection(item.routeSegment)}
          >
            <span className="settings-menu-item-icon" aria-hidden="true">
              {resolveSettingsMenuIcon(item.iconKey)}
            </span>
            <span className="settings-menu-item-label">{item.label}</span>
            <StageBadge stage={item.stage} />
          </button>
        ))}
      </nav>
    </div>
  );

  const activeItem = visibleMenuItems.find((item) => item.routeSegment === selectedSection);
  const pageTitle = activeItem?.label ?? 'Settings';

  if (!isKnownSection) return <NotFoundPage />;

  // Main pane
  const main = (
    <MainContentShell title={pageTitle}>
      {selectedSection === 'me' && <ProfileSection />}
      {selectedSection === 'integrations' && <ConnectionsSection />}
      {selectedSection === 'connected-apps' && <ConnectedAppsSection />}
      {selectedSection === 'ai-credits' && isStageAvailable('alpha', workspace?.featureMinStage) && (
        <AiCreditsReport endpoint="/api/workspace/ai-credits" />
      )}
    </MainContentShell>
  );

  return (
    <AppShell
      headerShowModuleSelector={false}
      leftNav={leftNav}
      contextPane={contextPane}
      contextPaneDividerClassName="settings-contextpane-divider"
      main={main}
    />
  );
}

/**
 * SettingsPage - user settings interface.
 *
 * Route: /settings, /settings/:section
 * Accessible to all authenticated users regardless of workspace role.
 */
export function SettingsPage() {
  return <SettingsPageContent />;
}
